import { useCallback, useEffect, useState } from 'react';
import {
  AutocompleteArrayInput,
  BooleanInput,
  ReferenceArrayInput,
  TextInput,
  required,
  useDataProvider,
  useEditContext,
  useNotify,
  useRecordContext,
} from 'react-admin';

import { NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { genericfilterToQuery } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE, getFieldOnMainLanguage } from '@app/utils/languages';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Typography } from '@mui/material';
import { GridRowModesModel } from '@mui/x-data-grid';
import { randomId } from '@mui/x-data-grid-generator';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DownloadIcon from '@mui/icons-material/Download';

import { CustomDataGrid } from '@app/components/CustomDataGrid';
import { CustomRichText } from '@app/components/CustomRichText';
import { CustomDateInput } from '@app/components/common/CustomDateInput';
import { ImageSelector } from '@app/components/common/ImageSelector';
import { ASPECT_RATIO_TYPES } from '@app/components/common/ImageSelector/ImageSelector';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { DefaultTable } from '@app/components/common/Table/DefaultTable';
import { CUSTOM_ICON_TYPES, ColumnTypes, GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { EventQuestions } from '../EventQuestions';

interface EventFormProps {
  title: string;
  sponsorLogo: File | null | undefined;
  eventLogo: File | null | undefined;
  agenda: GenericObject[];
  rowModesModel: GridRowModesModel;
  selectedQuestions: GenericObject[];
  reorderItems(dragIndex: number, hoverIndex: number): void;
  handleChangeSelectedQuestions(questions: GenericObject[]): void;
  handleChangeAgenda(values: GenericObject[]): void;
  handleChangeRowModesModel(values: GridRowModesModel): void;
  handleClickAddAgenda(): void;
  handleChangeSponsorLogo(logo: File): void;
  handleChangeEventLogo(logo: File): void;
  handleIsImport?: (isImport: boolean) => void;
  isImport?: boolean;
}

const InfoFieldContainerStyle = {
  display: 'flex',
};

const InfoFieldLabelStyle = {
  color: '#767676',
  fontSize: '13px',
  fontWeight: '700',
  marginLeft: '5px',
  marginRight: '20px',
};

const InfoFieldValueStyle = {
  fontSize: InfoFieldLabelStyle.fontSize,
};

export const EventForm = ({
  agenda,
  rowModesModel,
  handleChangeAgenda,
  selectedQuestions,
  reorderItems,
  handleChangeSelectedQuestions,
  handleChangeRowModesModel,
  handleClickAddAgenda,
  sponsorLogo,
  eventLogo,
  title,
  handleChangeSponsorLogo,
  handleChangeEventLogo,
  handleIsImport,
  isImport=false
}: EventFormProps) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { language, handleChangeLanguage } = useFormLanguage();
  const record = useRecordContext();
  const [bmID, setBmID] = useState<string>('');
  const [agendaAlreadySet, setAgendaAlreadySet] = useState(false);
  const [isAddQuestionsOpen, setIsAddQuestionsOpen] = useState(false);
  const [isQuestionsDefined, setIsQuestionsDefined] = useState(false);
  const [isIdValid, setIsIdValid] = useState(false);
  const [importedEvent, setImportedEvent] = useState<any>();
  const [unixStartTime, setUnixStartTime] = useState<any>(null);
  const [unixEndTime, setUnixEndTime] = useState<any>(null);
  const { refetch } = useEditContext();

  useEffect(() => {
    if (record?.agenda && !agendaAlreadySet) {
      handleChangeAgenda(record?.agenda.map((item: GenericObject) => ({ ...item, id: randomId() })));
      setAgendaAlreadySet(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, agendaAlreadySet]);

  const handleCloseAddQuestions = () => {
    setIsAddQuestionsOpen(false);
  };

  const updateRecord = useCallback(async () => {
    if (refetch && !isQuestionsDefined) {
      const response = await refetch();
      handleChangeSelectedQuestions(
        response?.data?.event_questions
          ? response?.data.event_questions
              .map((item: GenericObject) => ({
                ...(item.question as GenericObject),
                required: item.is_mandatory,
                position: item.position,
              }))
              .sort((a: GenericObject, b: GenericObject) => (a.position as number) - (b.position as number))
          : []
      );
      setIsQuestionsDefined(true);
    }
  }, [refetch, isQuestionsDefined, handleChangeSelectedQuestions]);

  useEffect(() => {
    updateRecord();
  }, [updateRecord]);

  const isQuestionSelected = (question: GenericObject) => {
    return !!selectedQuestions.find((item: GenericObject) => item.id === question.id);
  };

  const removeQuestion = (row: GenericObject) => {
    const filtered = selectedQuestions.filter((item: GenericObject) => item.id !== row.id);
    handleChangeSelectedQuestions(filtered);
  };

  const handleDelete = (row: GenericObject) => {
    const alreadySelected = isQuestionSelected(row);
    if (alreadySelected) {
      removeQuestion(row);
      return;
    }
  };

  const actions = [{ id: 'delete', icon: () => CUSTOM_ICON_TYPES.DELETE, action: handleDelete, label: 'Delete' }];

  const columns = [
    {
      label: 'QUESTION',
      key: 'question',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.question),
    },
    {
      label: 'REQUIRED',
      key: 'required',
      type: ColumnTypes.CHECKBOX,
      isPublished: (row: GenericObject) => row.required as boolean,
      toggleCheckbox: (row: GenericObject, required: boolean) => {
        const question = selectedQuestions.find((item) => item.id === row.id);
        if (question) {
          question.required = required;
          handleChangeSelectedQuestions([...selectedQuestions]);
        }
      },
    },
  ];

  const handleImportInputChange = async(type: string, bmIDe: string) => {
    setBmID(bmIDe);
    try {
      if(bmIDe === '') {
        setImportedEvent(null);
        return;
      };
      const eventResponse = await dataProvider.fetchEventByBMID('events',bmIDe);

      if(type === 'check') {
        await eventResponse.data? setIsIdValid(true) : setIsIdValid(false);
      }

      if(await eventResponse.data.id !== bmIDe ) {
        setImportedEvent(null);
        return;
      };

      if(type === 'import') {
        await eventResponse.data.id === bmIDe && setImportedEvent((prev) => {
          if(prev) {
            if(prev.id !== eventResponse.data.id) {
              return eventResponse.data;
            }else {
              return prev;
            }
          }else {
            return eventResponse.data;
          }
        })
      }
      notify('Big Marker ID is valid.', { type: 'success' });
    } catch (error: any) {
      notify('Invalid Big Marker ID !', { type: 'error' });
    } finally {

    }
  }

  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  const formatDMY = (date: string) => {
    const formatted = new Date(date);
    return `${formatted.getDate()}/${formatted.getMonth()+1}/${formatted.getFullYear()}`
  }

  const formatTime = (time: string) => {
    const formatted = new Date(time);
    let hour = formatted.getHours().toString()
    let min = formatted.getMinutes().toString()
    let hourFormat = formatted.getHours() < 13 ? "AM" : "PM";
    let singleDigitHH = formatted.getHours().toString().length < 2;
    let singleDigitMM = formatted.getMinutes().toString().length < 2;
    let formattedHour = singleDigitHH ? `0${hour}` : hour;
    let formattedMin = singleDigitMM ? `0${min}` : min;

    return `${formattedHour}:${formattedMin}${hourFormat}`
  }

  useEffect(() => {
    if (importedEvent?.start_time){
      const unix = Math.floor(new Date(importedEvent?.start_time).getTime())
      setUnixStartTime(unix);
    }
    if (importedEvent?.end_time){
      const unix = Math.floor(new Date(importedEvent?.end_time).getTime())
      setUnixEndTime(unix);
    }
  }, [importedEvent]);
  
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div>
        <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        {handleIsImport &&        
          <BooleanInput
            helperText={false}
            label="Import by Bigmarker ID"
            source="isImport"
            defaultValue={false}
            parse={(value: boolean) => {
              if (value === true) {
                handleIsImport && handleIsImport(true);
                return true;
              } else {
                handleIsImport && handleIsImport(false);
                return false;
              }
            }}
          />
        }
        <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

        <Grid spacing={2} xl={8} lg={12} md={12} xs={12} container item>
          {isImport &&
            <>
              <Grid item xs={6}>
                <TextInput
                  variant="outlined"
                  label="BigMarker Conference ID"
                  helperText={false}
                  source="big_marker_id"
                  name="big_marker_id"
                  onChange={(e) => {
                    const value = e.target.value;
                    setIsIdValid(false); 
                    setImportedEvent(null);         
                    (() => {
                      if (debounceTimeout) clearTimeout(debounceTimeout);            
                      if (!value) {
                        debounceTimeout = null;
                        return;
                      }
                      debounceTimeout = setTimeout(() => {
                        handleImportInputChange('check', value);
                        debounceTimeout = null;
                      }, 2000);
                    })();
                  }}
                  fullWidth
                  focused
                  disabled={!isImport}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  data-cy="addNew"
                  variant='contained'
                  color={'primary'}
                  startIcon={<DownloadIcon/>}
                  onClick={() => handleImportInputChange('import', bmID)}
                  sx={{ marginLeft: 2, paddingTop: '5px', paddingBottom: '5px', marginTop: '10px' }}
                  disabled={(!isIdValid || !isImport)}
                  >
                  IMPORT
                </Button>
              </Grid>
            </>      
          }
          {!handleIsImport &&
            <>
              <Grid item xs={12}>
                <div style={InfoFieldContainerStyle}>
                  <span style={InfoFieldLabelStyle}>Big Marker ID</span>
                  <span style={InfoFieldValueStyle}>{record?.big_marker_id ?? ''}</span>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div style={InfoFieldContainerStyle}>
                  <span style={InfoFieldLabelStyle}>Registration URL</span>
                  <span style={InfoFieldValueStyle}>{record?.registration_url ?? ''}</span>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div style={InfoFieldContainerStyle}>
                  <span style={InfoFieldLabelStyle}>Event URL</span>
                  <span style={InfoFieldValueStyle}>{record?.event_url ?? ''}</span>
                </div>           
              </Grid>
            </>
          }         
          <Grid item xs={12}>            
            <MultiLingualField
              Component={TextInput}
              language={language}
              source="title"
              name="title"
              label="Title"
              helperText={false}
              variant="outlined"
              fullWidth
              validate={[required()]}
              defaultValue={importedEvent?.title}
            />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={CustomRichText}
              language={language}
              multiline
              rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
              fullWidth
              label="Description"
              validate={[required()]}
              source="description"
              defaultValue={importedEvent?.purpose}
            />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={CustomRichText}
              language={language}
              validate={[required()]}
              multiline
              rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
              fullWidth
              label="About"
              source="about"
              defaultValue={importedEvent?.purpose}
            />
          </Grid>
          <Grid item xs={6}>
            {isImport ? 
              <TextInput
                variant="outlined"
                label="Event Date"
                helperText={false}
                source="event_date"
                name="event_date"
                format={() => importedEvent?.start_time ? formatDMY(importedEvent?.start_time) : ""}
                defaultValue={importedEvent?.start_time}
                fullWidth
                focused
                disabled={isImport}
              />
              :
              <CustomDateInput 
                source="event_date" 
                label="Event Date"                 
              />
            }
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            {isImport ? 
              <TextInput
                variant="outlined"
                label="Start Time"
                helperText={false}
                source="start_time"
                name="start_time"
                format={() => importedEvent?.start_time ? formatTime(importedEvent?.start_time) : ""}
                defaultValue={unixStartTime}
                fullWidth
                focused
                disabled={isImport}
              />
              :
              <CustomDateInput source="start_time" label="Start Time" timeOnly />
            }
          </Grid>
          <Grid item xs={6}>
            {isImport ? 
              <TextInput
                variant="outlined"
                label="End Time"
                helperText={false}
                source="end_time"
                name="end_time"
                format={() => importedEvent?.end_time ? formatTime(importedEvent?.end_time)  : ""}
                defaultValue={unixEndTime}
                fullWidth
                focused
                disabled={isImport}
              />
              :
              <CustomDateInput source="end_time" label="End Time" timeOnly />
            }
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={CustomRichText}
              language={language}
              label="Terms & Conditions"
              source="terms_and_conditions"
            />
          </Grid>
          <Grid item xs={6}>
            <ImageSelector
              getImageId={(record: GenericObject) => record?.expert_photo_file_base_id as string}
              getImagePath={(record: GenericObject) =>
                (record?.expert_photo_file_base as GenericObject)?.path as string
              }
              nonRequired
              label="Sponsor"
              aspectRatio={ASPECT_RATIO_TYPES.ALL}
              image={sponsorLogo}
              handleChangeImage={handleChangeSponsorLogo}
            />
          </Grid>
          <Grid item xs={6}>
            <ImageSelector
              label="Event"
              nonRequired
              aspectRatio={ASPECT_RATIO_TYPES.THUMBNAIL}
              getImageId={(record: GenericObject) => record?.event_image_file_base_id as string}
              getImagePath={(record: GenericObject) => (record?.event_image_file_base as GenericObject)?.path as string}
              image={eventLogo}
              handleChangeImage={handleChangeEventLogo}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput variant="outlined" helperText={false} label="Video Preview URL" source="preview_url" fullWidth />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              Component={TextInput}
              language={language}
              source="key_points_title"
              label="Key Points - Title"
              helperText={false}
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={CustomRichText}
              language={language}
              multiline
              rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
              fullWidth
              label="Key Points - Body"
              source="key_points_body"
            />
          </Grid>
          <Grid item xs={12}>
            <ReferenceArrayInput source="expert_ids" reference="experts">
              <AutocompleteArrayInput
                variant="outlined"
                autoHighlight
                filterToQuery={genericfilterToQuery}
                helperText={false}
                label="Experts"
                fullWidth
                optionText={(record) => `${record.first_name[DEFAULT_LANGUAGE]} ${record.last_name[DEFAULT_LANGUAGE]}`}
              />
            </ReferenceArrayInput>
          </Grid>
          <Grid item xs={12}>
            <ReferenceArrayInput
              source="company_ids"
              reference="companies"
              sort={{ field: 'short_name', order: 'ASC' }}>
              <AutocompleteArrayInput
                variant="outlined"
                helperText={false}
                autoHighlight
                label="Companies"
                filterToQuery={genericfilterToQuery}
                fullWidth
                optionText={(record) => record.name[DEFAULT_LANGUAGE]}
              />
            </ReferenceArrayInput>
          </Grid>
          <Grid item xs={12}>
            <ReferenceArrayInput source="topic_ids" reference="taxonomies">
              <AutocompleteArrayInput
                variant="outlined"
                helperText={false}
                autoHighlight
                label="Topics"
                fullWidth
                optionText={(record) => record.category_name[DEFAULT_LANGUAGE]}
              />
            </ReferenceArrayInput>
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              Component={TextInput}
              language={language}
              source="keywords"
              label="Keywords"
              helperText={false}
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={() => setIsAddQuestionsOpen(true)} startIcon={<AddIcon />}>
              ADD QUESTIONS
            </Button>
          </Grid>
          <Grid item xs={12}>
            <DefaultTable
              title={title}
              reorderRecord={reorderItems}
              orderable
              noPadding
              rows={selectedQuestions}
              columns={columns}
              actions={actions}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomDataGrid
              rows={agenda}
              handleChangeRows={handleChangeAgenda}
              rowModesModel={rowModesModel}
              handleChangeRowModesModel={handleChangeRowModesModel}
              handleNewRecord={handleClickAddAgenda}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid item xs={12}>
              <BooleanInput
                helperText={false}
                label="Published"
                source="status"
                defaultValue={'INACTIVE'}
                parse={(value: boolean) => {
                  if (value === true) {
                    return 'ACTIVE';
                  } else {
                    return 'INACTIVE';
                  }
                }}
                format={(value: string) => {
                  if (value === 'ACTIVE') {
                    return true;
                  } else {
                    return false;
                  }
                }}
              />
            </Grid>
          </Grid>
          {isAddQuestionsOpen && (
            <EventQuestions
              isQuestionSelected={isQuestionSelected}
              removeQuestion={removeQuestion}
              open={isAddQuestionsOpen}
              handleClose={handleCloseAddQuestions}
              selected={selectedQuestions}
              handleChangeSelected={handleChangeSelectedQuestions}
            />
          )}
        </Grid>        
      </div>
    </LocalizationProvider>
  );
};
